import {
	CASCADING_SELECT_CF_TYPE,
	MULTI_CHECKBOXES_CF_TYPE,
	MULTI_SELECT_CF_TYPE,
	RADIO_BUTTONS_CF_TYPE,
	SELECT_CF_TYPE,
} from '@atlassian/jira-platform-field-config/src/index.tsx';
import type { OptionType } from '../../../../types/layout-items.tsx';
import type { ValidationError, FieldValidationInput } from '../../../../types/validation.tsx';
import { POSITION_FIELD_OPTIONS } from '../../constants.tsx';
import { messages } from './messages.tsx';

const valid: ValidationError[] = [];

const fieldsThatSupportOptions: ReadonlyArray<string> = [
	SELECT_CF_TYPE,
	MULTI_CHECKBOXES_CF_TYPE,
	CASCADING_SELECT_CF_TYPE,
	MULTI_SELECT_CF_TYPE,
	RADIO_BUTTONS_CF_TYPE,
];

const doesFieldTypeSupportOptions = (fieldType: string): boolean =>
	fieldsThatSupportOptions.includes(fieldType);

const checkForDuplicatedOptionsPerParentExternalUuid = (
	options: ReadonlyArray<OptionType>,
): ReadonlyArray<ValidationError> => {
	// `seenValuesMap` is a map of parentExternalUuids (can be null) -> a set of seen values
	// a null parentExternalUuid represents top level options
	const seenValuesMap = new Map<string | null, Set<string>>();
	const checkValue = (parentExternalUuid: string | null, value: string): boolean =>
		Boolean(seenValuesMap.get(parentExternalUuid)?.has(value));
	const rememberValue = (parentExternalUuid: string | null, value: string): void => {
		let seenValuesForParent = seenValuesMap.get(parentExternalUuid);
		if (!seenValuesForParent) {
			seenValuesForParent = new Set();
			seenValuesMap.set(parentExternalUuid, seenValuesForParent);
		}
		seenValuesForParent.add(value);
	};

	const errors: ValidationError[] = [];
	options.forEach((option: OptionType) => {
		const { parentExternalUuid, value } = option;
		if (checkValue(parentExternalUuid ?? null, value)) {
			errors.push({
				position: POSITION_FIELD_OPTIONS,
				messageDescriptor: messages.errorKeyDuplicate,
				positionId: {
					optionOptimisticId: option.optimisticId,
				},
			});
		} else {
			rememberValue(parentExternalUuid ?? null, value);
		}
	});
	return errors;
};

export const optionsMustBeUniquePerParent = ({
	field,
}: FieldValidationInput): ReadonlyArray<ValidationError> => {
	if (!doesFieldTypeSupportOptions(field.type)) {
		return valid;
	}
	if (!field.operations.canModifyOptions) {
		return valid;
	}
	if (field.options == null) {
		/*
		 * TMP SWIFT used to require that all option-fields must have at least one option,
		 * but the global settings page doesn't do that, and it allows having zero options.
		 * So let's do the same here.
		 */
		return valid;
	}

	return checkForDuplicatedOptionsPerParentExternalUuid(field.options);
};
